import {
  X,
  MapPin,
  Users,
  Clock,
  User,
  CheckCircle2,
  Loader2,
  Trash2,
  UserPlus,
  Shield,
  AlertCircle,
} from 'lucide-react';
import { useState } from 'react';

import type { ScheduleEvent, ClassEnrollment } from '../../../services/schedule';
import { useClassEnrollments } from '../../../hooks/schedule/useClassEnrollments';
import { useMyEnrollments } from '../../../hooks/schedule/useMyEnrollments';
import { useScheduleSettings } from '../../../hooks/schedule/useScheduleSettings';

interface ClassDetailsModalProps {
  event: ScheduleEvent;
  isOwner: boolean;
  onClose: () => void;
  onEdit?: () => void;
}

// Format a stored local time string (HH:MM:SS) for display — no timezone conversion needed
// because start_time_local is already in the gym's local time.
function formatTimeLocal(local: string | null | undefined): string {
  if (!local) return '';
  const [h, m] = local.split(':');
  // Construct a local Date from the components so toLocaleTimeString formats correctly
  return new Date(2000, 0, 1, Number(h), Number(m)).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Fallback for legacy events that predate the start_time_local column
function formatTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '';
  const mins = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ─── Student row ─────────────────────────────────────────────────────────────

function StudentRow({
  enrollment,
  isOwner,
  onCheckIn,
  onRemove,
  isCheckingIn,
  isRemoving,
}: {
  enrollment: ClassEnrollment;
  isOwner: boolean;
  onCheckIn: () => void;
  onRemove: () => void;
  isCheckingIn: boolean;
  isRemoving: boolean;
}) {
  const name =
    enrollment.first_name || enrollment.last_name
      ? `${enrollment.first_name ?? ''} ${enrollment.last_name ?? ''}`.trim()
      : enrollment.email ?? 'Unknown';

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#363232] last:border-0">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded-full bg-[#504A4A] flex items-center justify-center shrink-0">
          {enrollment.avatar_url ? (
            <img src={enrollment.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <User size={14} className="text-[#A9A3A3]" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-[#EBFFEE] text-sm font-medium truncate">{name}</p>
          {enrollment.attended && (
            <p className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle2 size={10} /> Checked in
            </p>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {!enrollment.attended && (
            <button
              onClick={onCheckIn}
              disabled={isCheckingIn}
              className="flex items-center gap-1 text-xs bg-green-600/20 hover:bg-green-600/40 text-green-400 px-2 py-1 rounded transition-colors disabled:opacity-50"
            >
              {isCheckingIn ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
              Check in
            </button>
          )}
          <button
            onClick={onRemove}
            disabled={isRemoving}
            className="p-1 text-[#A9A3A3] hover:text-red-400 transition-colors disabled:opacity-50"
          >
            {isRemoving ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Add student panel (owner) ────────────────────────────────────────────────

function AddStudentPanel({
  classId,
  existingStudentIds,
  onAdded,
}: {
  classId: string;
  existingStudentIds: Set<string>;
  onAdded: () => void;
}) {
  const { members } = useScheduleSettings();
  const { addStudent, isAdding } = useClassEnrollments(classId);
  const [search, setSearch] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtered = members.filter((m) => {
    if (existingStudentIds.has(m.id)) return false;
    const q = search.toLowerCase();
    return (
      m.first_name?.toLowerCase().includes(q) ||
      m.last_name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q)
    );
  });

  const handleAdd = async (studentId: string) => {
    setAddingId(studentId);
    setError(null);
    try {
      await addStudent({ studentId });
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add student');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="mt-3 border border-[#504A4A] rounded-lg p-3">
      <p className="text-[#EBFFEE] text-sm font-semibold mb-2">Add Member</p>
      <input
        type="text"
        placeholder="Search by name or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full bg-[#111010] border border-[#504A4A] rounded-md px-3 py-1.5 text-sm text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421] mb-2"
      />
      {error && <p className="text-red-400 text-xs mb-2">{error}</p>}
      <div className="max-h-36 overflow-y-auto space-y-1">
        {filtered.length === 0 ? (
          <p className="text-[#A9A3A3] text-xs py-2 text-center">
            {search ? 'No members found' : 'All members already enrolled'}
          </p>
        ) : (
          filtered.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-1.5">
              <div>
                <p className="text-[#EBFFEE] text-sm">
                  {m.first_name} {m.last_name}
                </p>
                {m.email && <p className="text-[#A9A3A3] text-xs">{m.email}</p>}
              </div>
              <button
                onClick={() => handleAdd(m.id)}
                disabled={isAdding && addingId === m.id}
                className="flex items-center gap-1 text-xs bg-[#9B1421]/30 hover:bg-[#9B1421]/40 text-[#EBFFEE] px-2 py-1 rounded transition-colors disabled:opacity-50"
              >
                {isAdding && addingId === m.id ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : (
                  <UserPlus size={10} />
                )}
                Add
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function ClassDetailsModal({ event, isOwner, onClose, onEdit }: ClassDetailsModalProps) {
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { enrollments, isLoading: loadingRoster, checkInStudent, removeStudent } =
    useClassEnrollments(isOwner ? event.event_id : null);

  const {
    enrolledClassIds,
    enrollmentByClassId,
    enroll,
    isEnrolling,
    cancel,
    isCancelling,
    checkIn,
    isCheckingIn,
  } = useMyEnrollments();

  const myEnrollment = enrollmentByClassId[event.event_id];
  const isEnrolled = enrolledClassIds.has(event.event_id);
  const isCheckedIn = myEnrollment?.attended === true;

  const isFull =
    event.max_capacity !== null &&
    (event.current_registrations ?? 0) >= event.max_capacity;

  const handleEnroll = async () => {
    setActionError(null);
    try {
      await enroll({ classId: event.event_id });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to enroll');
    }
  };

  const handleCancel = async () => {
    if (!myEnrollment) return;
    setActionError(null);
    try {
      await cancel({ enrollmentId: myEnrollment.id });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to cancel');
    }
  };

  const handleSelfCheckIn = async () => {
    if (!myEnrollment) return;
    setActionError(null);
    try {
      await checkIn({ enrollmentId: myEnrollment.id });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to check in');
    }
  };

  const existingStudentIds = new Set(enrollments.map((e) => e.student_id));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#1A1717] border border-[#504A4A] rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#363232] shrink-0">
          <div className="min-w-0 pr-3">
            <h2 className="text-[#EBFFEE] font-bold text-xl leading-tight">
              {event.sport_name ?? 'Class'}
            </h2>
            <p className="text-[#A9A3A3] text-sm mt-0.5">{formatDate(event.start_time)}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isOwner && onEdit && (
              <button
                onClick={onEdit}
                className="text-xs text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#363232] hover:bg-[#504A4A] px-3 py-1.5 rounded-lg transition-colors"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Meta info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-[#A9A3A3]">
              <Clock size={14} className="text-[#E8535E] shrink-0" />
              <span>
                {formatTimeLocal(event.start_time_local) || formatTime(event.start_time)} – {formatTimeLocal(event.end_time_local) || formatTime(event.end_time)}
                <span className="text-[#504A4A] ml-1">
                  ({formatDuration(event.start_time, event.end_time)})
                </span>
              </span>
            </div>

            {event.max_capacity !== null && (
              <div className="flex items-center gap-2 text-sm text-[#A9A3A3]">
                <Users size={14} className="text-[#E8535E] shrink-0" />
                <span>
                  {event.current_registrations ?? 0} / {event.max_capacity} spots
                  {isFull && <span className="text-red-400 ml-1 font-medium">Full</span>}
                </span>
              </div>
            )}

            {event.location_name && (
              <div className="flex items-center gap-2 text-sm text-[#A9A3A3]">
                <MapPin size={14} className="text-[#E8535E] shrink-0" />
                <span>{event.location_name}</span>
              </div>
            )}

            {event.instructor_names && event.instructor_names.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-[#A9A3A3]">
                <Shield size={14} className="text-[#E8535E] shrink-0" />
                <span className="truncate">{event.instructor_names.join(', ')}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          {(event.skill_level_name || event.age_bracket_name) && (
            <div className="flex flex-wrap gap-2">
              {event.skill_level_name && (
                <span className="text-xs bg-[#363232] border border-[#504A4A] text-[#EBFFEE] px-2.5 py-1 rounded-full">
                  {event.skill_level_name}
                </span>
              )}
              {event.age_bracket_name && (
                <span className="text-xs bg-[#363232] border border-[#504A4A] text-[#EBFFEE] px-2.5 py-1 rounded-full">
                  {event.age_bracket_name}
                </span>
              )}
            </div>
          )}

          {/* Description */}
          {event.event_description && (
            <p className="text-[#A9A3A3] text-sm leading-relaxed">{event.event_description}</p>
          )}

          {/* Error */}
          {actionError && (
            <div className="flex items-center gap-2 bg-red-900/20 border border-red-600/40 rounded-lg px-3 py-2">
              <AlertCircle size={14} className="text-red-400 shrink-0" />
              <p className="text-red-300 text-sm">{actionError}</p>
            </div>
          )}

          {/* ── Student actions (non-owner) ── */}
          {!isOwner && (
            <div className="space-y-2">
              {isCheckedIn ? (
                <div className="flex items-center justify-center gap-2 bg-green-600/15 border border-green-600/30 rounded-xl py-3">
                  <CheckCircle2 size={16} className="text-green-400" />
                  <span className="text-green-400 font-semibold text-sm">Checked In</span>
                </div>
              ) : isEnrolled ? (
                <div className="space-y-2">
                  <button
                    onClick={handleSelfCheckIn}
                    disabled={isCheckingIn}
                    className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {isCheckingIn ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={16} />
                    )}
                    Check In
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isCancelling}
                    className="w-full text-[#A9A3A3] hover:text-red-400 text-sm py-2 transition-colors disabled:opacity-50"
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel Registration'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleEnroll}
                  disabled={isEnrolling || isFull}
                  className="w-full flex items-center justify-center gap-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEnrolling ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Enrolling...
                    </>
                  ) : isFull ? (
                    'Class Full'
                  ) : (
                    'Enroll in Class'
                  )}
                </button>
              )}
            </div>
          )}

          {/* ── Owner roster ── */}
          {isOwner && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[#EBFFEE] font-semibold text-sm">
                  Roster ({enrollments.length}
                  {event.max_capacity ? `/${event.max_capacity}` : ''})
                </p>
                <button
                  onClick={() => setShowAddStudent((v) => !v)}
                  className="flex items-center gap-1 text-xs text-[#E8535E] hover:text-[#EBFFEE] transition-colors"
                >
                  <UserPlus size={12} />
                  {showAddStudent ? 'Done' : 'Add'}
                </button>
              </div>

              {showAddStudent && (
                <AddStudentPanel
                  classId={event.event_id}
                  existingStudentIds={existingStudentIds}
                  onAdded={() => setShowAddStudent(false)}
                />
              )}

              {loadingRoster ? (
                <div className="flex justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-[#A9A3A3]" />
                </div>
              ) : enrollments.length === 0 ? (
                <p className="text-[#A9A3A3] text-sm text-center py-4">No students enrolled yet.</p>
              ) : (
                <div>
                  {enrollments.map((enrollment) => (
                    <StudentRow
                      key={enrollment.id}
                      enrollment={enrollment}
                      isOwner={isOwner}
                      onCheckIn={async () => {
                        setActionError(null);
                        try {
                          await checkInStudent({ enrollmentId: enrollment.id });
                        } catch (e) {
                          setActionError(e instanceof Error ? e.message : 'Check-in failed');
                        }
                      }}
                      onRemove={async () => {
                        setActionError(null);
                        try {
                          await removeStudent({ enrollmentId: enrollment.id });
                        } catch (e) {
                          setActionError(e instanceof Error ? e.message : 'Remove failed');
                        }
                      }}
                      isCheckingIn={false}
                      isRemoving={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
